/**
 * Client-side pagination for the collection product grid.
 *
 * The Liquid section fetches up to 250 products per collection view and,
 * when a size filter is active, drops products that have no stock in the
 * selected size BEFORE paginating. Because that drop happens before paging,
 * Shopify's own page-by-page pagination can no longer be trusted to reflect
 * how many products actually remain, so pagination is handled here instead:
 * all eligible product cards are already in the DOM (hidden beyond the
 * first page via inline style from Liquid), and this script slices them
 * into pages of `data-page-size` items, builds the page nav, and re-renders
 * whenever the grid content changes (e.g. after a filter change swaps in a
 * new server-rendered set of eligible products).
 */
(function () {
  var renderers = [];

  function getItems(grid) {
    return Array.prototype.slice.call(grid.children).filter(function (el) {
      return el.tagName === 'LI';
    });
  }

  function buildUrl(page) {
    var params = new URLSearchParams(window.location.search);
    if (page <= 1) {
      params.delete('page');
    } else {
      params.set('page', page);
    }
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function getRequestedPage(pages) {
    var params = new URLSearchParams(window.location.search);
    var requested = parseInt(params.get('page'), 10);
    if (!requested || requested < 1) requested = 1;
    if (requested > pages) requested = pages;
    return requested;
  }

  function chevronMarkup() {
    return (
      '<span class="svg-wrapper">' +
      '<svg viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg" fill="none">' +
      '<path d="M9.35 3.35a.5.5 0 0 0 0-.7L6.17.46a.5.5 0 1 0-.7.7L7.79 3.5 5.46 5.83a.5.5 0 0 0 .71.7l3.18-3.18z" fill="currentColor"/>' +
      '</svg></span>'
    );
  }

  function initPagination(container) {
    if (!container || container.dataset.paginationBound === 'true') return;
    container.dataset.paginationBound = 'true';

    function applyPage(items, pageSize, page, pages, scroll) {
      var start = (page - 1) * pageSize;
      var end = start + pageSize;
      items.forEach(function (el, index) {
        el.style.display = index >= start && index < end ? '' : 'none';
      });
      renderNav(pageSize, page, pages);
      if (scroll) {
        var target = document.getElementById('main-collection-filters') || container;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function renderNav(pageSize, current, pages) {
      var paginationSlot = container.querySelector('#CustomPagination');
      if (!paginationSlot) return;
      paginationSlot.innerHTML = '';
      if (pages <= 1) return;

      var nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('role', 'navigation');

      var list = document.createElement('ul');
      list.className = 'pagination__list list-unstyled';
      list.setAttribute('role', 'list');

      function addLink(page, options) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = buildUrl(page);
        var classes = ['pagination__item'];
        if (options.isCurrent) {
          classes.push('pagination__item--current', 'light');
          a.setAttribute('aria-current', 'page');
          a.setAttribute('role', 'link');
          a.setAttribute('aria-disabled', 'true');
        } else {
          classes.push('link');
        }
        if (options.arrowClass) classes.push(options.arrowClass, 'pagination__item-arrow', 'motion-reduce');
        a.className = classes.join(' ');
        a.innerHTML = options.arrowClass ? chevronMarkup() : String(page);
        a.addEventListener('click', function (event) {
          event.preventDefault();
          if (page === current) return;
          history.pushState({}, '', buildUrl(page));
          var grid = container.querySelector('#product-grid');
          if (!grid) return;
          var freshItems = getItems(grid);
          var freshPageSize = parseInt(grid.dataset.pageSize, 10) || pageSize;
          var freshPages = Math.max(1, Math.ceil(freshItems.length / freshPageSize));
          applyPage(freshItems, freshPageSize, page, freshPages, true);
        });
        li.appendChild(a);
        list.appendChild(li);
      }

      if (current > 1) addLink(current - 1, { arrowClass: 'pagination__item--next' });
      for (var i = 1; i <= pages; i++) {
        addLink(i, { isCurrent: i === current });
      }
      if (current < pages) addLink(current + 1, { arrowClass: 'pagination__item--prev' });

      nav.appendChild(list);
      paginationSlot.appendChild(nav);
    }

    function updateCount(total) {
      if (container.dataset.sizeFiltered !== 'true') return;
      var label = total === 1 ? total + ' product' : total + ' products';
      var countEl = document.getElementById('ProductCount');
      var countElDesktop = document.getElementById('ProductCountDesktop');
      if (countEl) countEl.textContent = label;
      if (countElDesktop) countElDesktop.textContent = label;
    }

    function render() {
      var grid = container.querySelector('#product-grid');
      if (!grid) return;
      var pageSize = parseInt(grid.dataset.pageSize, 10) || 16;
      var items = getItems(grid);
      var total = items.length;
      var pages = Math.max(1, Math.ceil(total / pageSize));
      var current = getRequestedPage(pages);
      applyPage(items, pageSize, current, pages, false);
      updateCount(total);
    }

    render();
    renderers.push({ container: container, render: render });

    var observer = new MutationObserver(function () {
      render();
    });
    observer.observe(container, { childList: true });
  }

  function initAll() {
    document.querySelectorAll('#ProductGridContainer').forEach(initPagination);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('popstate', function () {
    renderers.forEach(function (entry) {
      entry.render();
    });
  });
})();
